import type { GpuContext } from './context.js';
import type { GpuImage } from './resources.js';
import { FORMAT_BYTES } from './types.js';

const COPY_ALIGNMENT = 256;

/**
 * Copies a texture back to CPU memory, undoing WebGPU's 256-byte row padding.
 *
 * Used only where the CPU genuinely needs the pixels - PNG encoding on export
 * and palette extraction. The interactive path never calls this.
 */
export async function readTexture(ctx: GpuContext, image: GpuImage): Promise<Uint8Array> {
  const bytesPerTexel = FORMAT_BYTES[image.format];
  const unpaddedBytesPerRow = image.width * bytesPerTexel;
  const paddedBytesPerRow = Math.ceil(unpaddedBytesPerRow / COPY_ALIGNMENT) * COPY_ALIGNMENT;
  const size = paddedBytesPerRow * image.height;

  const staging = ctx.device.createBuffer({
    label: 'readback',
    size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });

  ctx.endPass();
  ctx.encoder().copyTextureToBuffer(
    { texture: image.texture },
    { buffer: staging, bytesPerRow: paddedBytesPerRow, rowsPerImage: image.height },
    { width: image.width, height: image.height },
  );
  ctx.flush();

  try {
    await staging.mapAsync(GPUMapMode.READ);
    const padded = new Uint8Array(staging.getMappedRange());
    const out = new Uint8Array(unpaddedBytesPerRow * image.height);
    for (let row = 0; row < image.height; row++) {
      out.set(
        padded.subarray(row * paddedBytesPerRow, row * paddedBytesPerRow + unpaddedBytesPerRow),
        row * unpaddedBytesPerRow,
      );
    }
    staging.unmap();
    return out;
  } finally {
    staging.destroy();
  }
}

/** Reads an `rgba8unorm` texture as `ImageData`. */
export async function readImageData(ctx: GpuContext, image: GpuImage): Promise<ImageData> {
  if (image.format !== 'rgba8unorm') {
    throw new Error(`[gpu-core] readImageData expects rgba8unorm, got ${image.format}`);
  }
  const bytes = await readTexture(ctx, image);
  // `ImageData` insists on a plain `ArrayBuffer`, so copy rather than alias.
  const clamped = new Uint8ClampedArray(bytes.length);
  clamped.set(bytes);
  return new ImageData(clamped, image.width, image.height);
}

export interface EncodeOptions {
  type?: 'image/png' | 'image/webp' | 'image/jpeg';
  quality?: number;
}

/**
 * Encodes an `rgba8unorm` texture to a Blob.
 *
 * PNG keeps the alpha channel, which is the whole point for a cut-out; WebP is
 * offered for previews where file size matters more than lossless edges.
 */
export async function encodeImage(
  ctx: GpuContext,
  image: GpuImage,
  options: EncodeOptions = {},
): Promise<Blob> {
  const data = await readImageData(ctx, image);
  const canvas = new OffscreenCanvas(image.width, image.height);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('[gpu-core] could not acquire a 2D context for encoding');
  context.putImageData(data, 0, 0);
  return canvas.convertToBlob({
    type: options.type ?? 'image/png',
    quality: options.quality,
  });
}
